export const ACTIVITY_DATA_ITEM_KEY = "activityId";
export const ACTIVITY_SELECTED_FIELD = "selected";

export function getActivitiesToDelete(dataItem, selectedState, listData = []) {
    const selectedIds = Object.keys(selectedState).filter(
        (key) => selectedState[key],
    );
    if (selectedIds.length > 1 && selectedState[dataItem.activityId]) {
        return listData.filter((item) => selectedState[item.activityId]);
    }
    return [dataItem];
}

export function getDeleteConfirmationMessage(items) {
    const itemsArray = Array.isArray(items) ? items : [items];
    if (itemsArray.length > 1) {
        return `Are you sure you want to delete ${itemsArray.length} selected activities?`;
    }
    return `Are you sure you want to delete activity '${itemsArray[0]?.details}'?`;
}

export function getDeleteSuccessMessage(items) {
    const itemsArray = Array.isArray(items) ? items : [items];
    return itemsArray.length > 1
        ? `${itemsArray.length} activities deleted successfully`
        : "Activity deleted successfully";
}

export function resolveDuplicateActivityId(sourceActivityOrId) {
    return typeof sourceActivityOrId === "object"
        ? sourceActivityOrId?.activityId
        : sourceActivityOrId;
}

export function mergeDuplicateActivityData(apiData, sourceRow) {
    if (!sourceRow) return apiData || {};
    return {
        ...(apiData || {}),
        documentId: apiData?.documentId ?? sourceRow.documentId,
        documentName: apiData?.documentName ?? sourceRow.documentName,
        documentFile: apiData?.documentFile ?? sourceRow.documentFile,
        fileId: apiData?.fileId ?? sourceRow.fileId,
    };
}

export function truncateFileName(fileName) {
    if (!fileName) return;
    const extensionIndex = fileName.lastIndexOf(".");

    if (extensionIndex === -1) {
        return fileName.length > 8 ? fileName.substring(0, 18) : fileName;
    }

    const extension = fileName.substring(extensionIndex);

    if (fileName.length > 8) {
        return fileName.substring(0, 7 - extension.length) + extension;
    }

    return fileName;
}

export function getStatusClass(status) {
    switch (status) {
        case "Posted":
            return "bg-success";
        case "Rejected":
            return "bg-danger";
        default:
            return "bg-secondary";
    }
}

export function getActivitySubtypeStatus(activity) {
    if (
        activity?.subType === "ChitRequest" ||
        activity?.subType === "BillRequest"
    ) {
        return activity.subTypeStatus || "";
    }
    return "";
}

export function getRowStyle(dataItem, activityTypes = []) {
    const activityType = activityTypes.find(
        (type) => type.activityTypeID === dataItem.typeId,
    );

    if (
        activityType &&
        activityType.rowStyle &&
        typeof activityType.rowStyle === "string"
    ) {
        const match = activityType.rowStyle.match(/backgroundColor:['"]([^'"]+)['"]/);
        if (match?.[1]) {
            return { backgroundColor: match[1] };
        }
    }

    return {};
}

export function getSelectedDocumentIds(selectedState, listData = []) {
    return Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
            const selectedItem = listData.find((item) => item.activityId == key);
            return selectedItem?.documentId;
        })
        .filter((id) => id);
}

export function getSelectedDocumentsForShare(selectedState, listData = []) {
    return Object.keys(selectedState)
        .filter((key) => selectedState[key])
        .map((key) => {
            const selectedItem = listData.find((item) => item.activityId == key);
            return {
                id: selectedItem?.documentId,
                name: selectedItem?.documentName,
            };
        })
        .filter((item) => item.id && item.name);
}

export function buildMappedGridData(
    list,
    selectedState,
    idGetter,
    selectedField = ACTIVITY_SELECTED_FIELD,
) {
    return {
        data: list?.data?.map((item) => ({
            ...item,
            [selectedField]: selectedState[idGetter(item)],
        })),
        total: list?.total,
    };
}

export function isEmailFile(fileName = "") {
    const ext = fileName.split(".").pop()?.toLowerCase();
    return ext === "eml" || ext === "msg";
}

export function getEmailFileExt(fileName = "") {
    return fileName.split(".").pop()?.toLowerCase();
}

export function extractEmailDomain(emailValue) {
    if (!emailValue) return null;
    const m = String(emailValue).match(/<?([^\s<>]+@[^\s<>]+)>?/);
    if (!m) return null;
    const addr = m[1];
    const at = addr.lastIndexOf("@");
    if (at === -1) return null;
    return addr.slice(at + 1).trim().toLowerCase();
}

export function domainsMatch(a, b) {
    if (!a || !b) return false;
    const aa = String(a).trim().toLowerCase();
    const bb = String(b).trim().toLowerCase();
    if (aa === bb) return true;
    return aa.endsWith("." + bb) || bb.endsWith("." + aa);
}

export function resolveEmailActivityType(activityTypes, fromValue, companyDomains) {
    if (!Array.isArray(activityTypes) || activityTypes.length === 0) return null;
    const fromValueStr = (fromValue ?? "").toString().trim();
    const domainList = (Array.isArray(companyDomains)
        ? companyDomains
        : String(companyDomains ?? "").split(",")
    )
        .map((d) => d.trim())
        .filter(Boolean);
    let code;
    if (!fromValueStr || !fromValueStr.includes("@")) {
        code = "EO";
    } else {
        const fromDomain = extractEmailDomain(fromValueStr);
        const matches = domainList.some((d) => domainsMatch(fromDomain, d));
        code = matches ? "EO" : "EI";
    }
    const found = activityTypes.find((t) => t.code === code);
    if (found) return found;
    return activityTypes.find((t) => t.code === "EO") || null;
}

export function parseDownloadFilename(response, asPDF = false) {
    let fileName;
    const contentDisposition = response.headers["content-disposition"];

    if (response.data?.filename) {
        fileName = response.data.filename;
    } else if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        if (fileNameMatch?.[1]) {
            fileName = fileNameMatch[1].replace(/['"]/g, "");
        }
    }
    if (!fileName) {
        fileName =
            (response.data?.fileID || "downloaded-document") + (asPDF ? ".pdf" : "");
    }
    return fileName;
}

export function downloadBlobAsFile(data, fileName) {
    const blob = new Blob([data]);
    const url = window.URL.createObjectURL(blob);
    const tempLink = document.createElement("a");
    tempLink.href = url;
    tempLink.setAttribute("download", fileName);
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    window.URL.revokeObjectURL(url);
}

export function applyActivitiesDragTransfer(
    event,
    dataItem,
    { listData = [], selectedState, idGetter, defaultMatterId },
) {
    const selectedItems = listData.filter((item) => selectedState[idGetter(item)]);

    const itemsToDrag =
        selectedItems.length > 0 && selectedState[idGetter(dataItem)]
            ? selectedItems
            : [dataItem];

    const selectedDocs = itemsToDrag
        .filter((item) => item.documentId)
        .map((item) => ({
            documentId: item.documentId,
            documentName: item.documentName || item.details,
            matterId: item.matterId || defaultMatterId,
            activityId: item.activityId,
            details: item.details,
        }));

    if (selectedDocs.length === 0) {
        event.preventDefault();
        return;
    }

    const payload = {
        documents: selectedDocs.map((d) => ({
            documentId: d.documentId,
            documentName: d.documentName,
            matterId: d.matterId,
            activityId: d.activityId,
        })),
    };

    event.dataTransfer.setData(
        "text/plain",
        "LINQ_CASE_ACTIVITIES:" + JSON.stringify(payload),
    );
    event.dataTransfer.effectAllowed = "copy";
}
